import { useContext, useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'

import { API } from 'API'
import { CasesPageContext } from 'containers/CasesPage/CasesPageContext'
import { RequestToasterContext } from 'containers/Providers'
import { Case, CaseStatusMatrix } from 'domains/case'
import { ComboType } from 'domains/case/ComboType'
import { StandardErrorResponse } from 'types/APIResponses'
import { isNotNil } from 'utils/isNotNil'

interface RollbackModalProps {
  isRollbackDialogOpen: boolean
  changeRollbackDialogStateTo: (it: boolean) => void
}

export const RollbackModal = ({
  isRollbackDialogOpen,
  changeRollbackDialogStateTo,
}: RollbackModalProps) => {
  const { casesPageState, getCase } = useContext(CasesPageContext)
  const { caseStatus, operator, id, childComboTreatment } = casesPageState.userCase as Case
  const { rollback } = casesPageState

  const { requestStatusRef } = useContext(RequestToasterContext)
  const [isRollbackSendEmail, setIsRollSendEmail] = useState(true)
  const [isRollbackHoldUnderstood, setIsRollbackHoldUnderstood] = useState(false)
  const [isRollBackHoldEnabled, setIsRollBackHoldEnabled] = useState(false)

  const handleSetIsRollbackSendEmail = (event: React.ChangeEvent<HTMLInputElement>) =>
    setIsRollSendEmail(event.target.checked)
  const handleSetIsRollbackHoldUnderstood = (event: React.ChangeEvent<HTMLInputElement>) =>
    setIsRollbackHoldUnderstood(event.target.checked)

  const [rollbackNotes, setRollbackNotes] = useState('')
  const handleSetRollbackNotes = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setRollbackNotes(event.target.value)

  const handleCloseDialog = () => {
    setIsRollSendEmail(true)
    changeRollbackDialogStateTo(false)
    setRollbackNotes('')
  }

  useEffect(() => {
    API.getFeatureFlags().then((data) => {
      if (rollback) {
        setIsRollBackHoldEnabled(
          data.FeatureFlags?.RollbackHold === true &&
            rollback[caseStatus] !== CaseStatusMatrix.SAVED,
        )
      }
    })
  }, [caseStatus, rollback])

  const handleRollbackStatus = () => {
    if (!isRollbackHoldUnderstood && isRollBackHoldEnabled) {
      alert('Please confirm the rollback and hold')
    } else if (rollbackNotes === '' || rollbackNotes.match(/^ *$/) !== null) {
      alert('Please enter notes')
    } else {
      changeRollbackDialogStateTo(false)
      requestStatusRef.current?.startProgress('Rolling back...', 'secondary')
      API.updateCaseById({
        id: id,
        rollbackStatus: true,
        rollbackNotes: rollbackNotes,
        rollbackSendEmail: isRollbackSendEmail,
      })
        .then(() => {
          getCase()
          requestStatusRef.current?.showAlert('Done', 'success')
        })
        .catch((error: StandardErrorResponse) => {
          requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
        })
    }
  }

  const handleCancelComboSpk = () => {
    changeRollbackDialogStateTo(false)
    requestStatusRef.current?.startProgress('Canceling combo SPK...', 'secondary')
    API.deleteComboTreatmentByParentId(id)
      .then(() => {
        getCase()
        requestStatusRef.current?.showAlert('Done', 'success')
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  if (childComboTreatment && childComboTreatment.comboType === ComboType.Spk) {
    return (
      <Modal isOpen={isRollbackDialogOpen} className="w-25" centered>
        <ModalHeader className="d-inline-flex flex-column w-100 align-items-center">
          <span>Cancel SPK case</span>
        </ModalHeader>
        <ModalBody>Are you sure?</ModalBody>
        <ModalFooter>
          <button type="button" onClick={handleCancelComboSpk}>
            Yes
          </button>
          <button type="button" onClick={handleCloseDialog}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    )
  }

  if (!rollback) {
    return <></>
  }

  const hasRollback = rollback?.hasOwnProperty(caseStatus) === true

  return (
    <Modal isOpen={isRollbackDialogOpen} className="w-25" centered>
      <ModalHeader className="d-inline-flex flex-column w-100 align-items-center">
        <span>Rollback status to</span>
        {hasRollback && <small className="float-right">{rollback[caseStatus]}</small>}
      </ModalHeader>
      <ModalBody>
        <textarea
          value={rollbackNotes}
          name="rollbackNotes"
          onChange={handleSetRollbackNotes}
          placeholder="notes (required)"
          rows={3}
          className="w-100"
        />
        {isNotNil(operator) && (
          <div>
            <input
              checked={isRollbackSendEmail}
              type="checkbox"
              id="rollbackSendEmail"
              onChange={handleSetIsRollbackSendEmail}
            />
            <label className="ml-1" htmlFor="rollbackSendEmail">
              Notify operator
            </label>
            <div>
              {`${operator.firstName} ${operator.lastName}`}
              {`< ${operator.email} >`}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="flex-nowrap align-items-center">
        {isRollBackHoldEnabled && (
          <div>
            <div className="text-danger small">
              Your case is put on hold due to the rollback, please login to DTX and review the hold
              and remove it accordingly.
            </div>
            <div className="d-flex">
              <input
                checked={isRollbackHoldUnderstood}
                type="checkbox"
                id="rollbackHoldUnderstood"
                onChange={handleSetIsRollbackHoldUnderstood}
              />
              <label className="small mb-0 ml-1" htmlFor="rollbackHoldUnderstood">
                I understand
              </label>
            </div>
          </div>
        )}
        <button type="button" onClick={handleRollbackStatus}>
          Rollback
        </button>
        <button type="button" onClick={handleCloseDialog}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  )
}
